import {
  IM_CSV_TABLE_HEADERS,
  IM_CSV_LEGACY_TABLE_HEADERS,
  ERROR_MESSAGES,
  IM_FILE_UPLOAD_TYPES,
  IM_REQUEST_TYPE_CODES,
  FILE_BADGE_VARIANTS,
  FILE_BADGE_VARIANT_DEFAULT,
  LEGACY_TASK_TYPE_LABEL_CHECKLIST_NAME,
  CHECKLIST_ONLY_TASK_TYPE_HEADER_VISIBILITY,
  DUE_DATE_RANGE_ERROR,
} from './constants';
import {
  findInfoOwnerByEmail,
  resolveBulkInfoOwner,
  sanitizePriority,
  sanitizeType,
  trimInput,
  validateUploadLink,
} from './bulkUploadValidation';

function isImCsvHeaderRow(row) {
  const serialized = JSON.stringify(row || []);
  return serialized === JSON.stringify(IM_CSV_TABLE_HEADERS)
    || serialized === JSON.stringify(IM_CSV_LEGACY_TABLE_HEADERS);
}

/**
 * Reads and parses a PBC Excel (.xlsx) file for bulk upload, returning structured data and errors.
 * @param {File} file - The Excel file to read.
 * @param {Array} reflist - Reference list for validation.
 * @param {Function} readExcelFile
 * @param {Array} [infoOwners] - Active and inactive owners. Email match keeps the stored name.
 * @returns {Promise<{pbcuploaddata: Array, pbcdataerror: Array, pbcerrorlist: Array, errorFlag: Boolean}>}
 */
export async function readCSVClient(file, reflist, readExcelFile, infoOwners = []) {
  if (!(file && file.name)) throw new Error(ERROR_MESSAGES.NO_FILE_PROVIDED);
  const fileExtension = file.name.split('.').pop().toLowerCase();
  if (fileExtension !== 'xlsx') throw new Error(ERROR_MESSAGES.INVALID_XLSX_FILE_EXTENSION);

  const csvRows = await readExcelFile(file, "");
  // Remove empty rows
  const filteredRows = csvRows.filter(row => row.some(cell => {
    const cellValue = cell != null ? cell.toString().trim() : '';
    return cellValue !== '';
  }));
  
  if (!isImCsvHeaderRow(filteredRows[0])) {
    throw new Error(ERROR_MESSAGES.INVALID_CSV_HEADERS(IM_CSV_TABLE_HEADERS));
  }
  
  // Early validation: Check required fields in all rows before processing
  for (let rowIndex = 1; rowIndex < filteredRows.length; rowIndex++) {
    const row = filteredRows[rowIndex];
    if ((row[2] || "").toString() === "") {
      throw new Error(`${ERROR_MESSAGES.DESCRIPTION_REQUIRED} at row ${rowIndex + 1}`);
    }
    const existingOwner = findInfoOwnerByEmail(infoOwners, row[4]);
    if ((row[3] || "").toString().trim() === "" && !existingOwner) {
      throw new Error(`${ERROR_MESSAGES.INFO_OWNER_NAME_REQUIRED} at row ${rowIndex + 1}`);
    }
    if ((row[4] || "").toString() === "") {
      throw new Error(`${ERROR_MESSAGES.INFO_OWNER_EMAIL_REQUIRED} at row ${rowIndex + 1}`);
    }
  }
  
  const groupNames = [];
  const groupData = {};
  const groupOrder = [];
  const errorData = {};
  let errorFlag = false;
  let groupStartNum = 1;

  // O(1) tag lookups removed — tag/location membership is validated server-side.

  for (let rowIndex = 1; rowIndex < filteredRows.length; rowIndex++) {
    const row = filteredRows[rowIndex];
    let groupName = rowIndex.toString();
    if (!groupName) {
      let newGroupName = `g${groupStartNum}`;
      while (groupNames.includes(newGroupName)) {
        groupStartNum++;
        newGroupName = `g${groupStartNum}`;
      }
      groupName = newGroupName;
      groupNames.push(groupName);
    }
    if (!groupNames.includes(groupName)) groupNames.push(groupName);
    if (!groupOrder.includes(groupName)) groupOrder.push(groupName);
    groupData[groupName] = groupData[groupName] || [];
    errorData[groupName] = errorData[groupName] || [];
    // Parse row data — Type + File Upload Link (aligned with single-request create)
    const checklistTags = String(row[0] || "").split(",").map(tag => tag.trim()).filter(Boolean);
    let entryErrors = [];
    const typeResult = sanitizeType(row[5], { allowed: IM_REQUEST_TYPE_CODES });
    const priorityResult = sanitizePriority(row[7]);
    const uploadlink = trimInput(row[6]);
    const resolvedOwner = resolveBulkInfoOwner(row[4], row[3], infoOwners);
    const fieldErrors = {};
    const entry = {
      group: groupName,
      ref: checklistTags,
      category: row[1],
      description: row[2],
      info_owner_name: resolvedOwner.name,
      info_owner_email: resolvedOwner.email,
      type: typeResult.value,
      uploadlink,
      priority: priorityResult.value,
      duedate: row[8],
      location: row[9] == null || row[9] === '' ? '' : String(row[9]).trim()
    };

    // Keep legacy file_upload flag for any consumers still reading it
    entry.file_upload = entry.type === IM_FILE_UPLOAD_TYPES.FILE_UPLOAD
      ? IM_FILE_UPLOAD_TYPES.FILE_UPLOAD
      : IM_FILE_UPLOAD_TYPES.TEXT;
    
    if (entry.duedate) {
      const parsedDate = parseDueDateValue(typeof entry.duedate === 'string' ? entry.duedate.trim() : entry.duedate);
      if (!parsedDate) {
        entryErrors.push(ERROR_MESSAGES.INVALID_DUE_DATE);
      } else {
        entry.duedate = formatDateToDMY(parsedDate);
      }
    }

    if (typeResult.error) {
      entryErrors.push(typeResult.error);
    }
    if (priorityResult.error) {
      entryErrors.push(priorityResult.error);
    }

    if (entry.type === IM_FILE_UPLOAD_TYPES.LINK) {
      const linkResult = validateUploadLink(uploadlink, { required: true });
      entry.uploadlink = linkResult.value;
      if (linkResult.error) {
        entryErrors.push(linkResult.error);
        fieldErrors.uploadlink = linkResult.error;
      }
    } else if (uploadlink) {
      entryErrors.push(ERROR_MESSAGES.UPLOAD_LINK_NOT_ALLOWED);
      entry.uploadlink = '';
    } else {
      entry.uploadlink = '';
    }

    // Validate location is provided (mandatory field)
    if (!entry.location || entry.location?.toString()?.trim() === '') {
      entryErrors.push(ERROR_MESSAGES.LOCATION_REQUIRED);
    }

    // Tag/location membership and actor access are validated via validateBulkPbc.
    
    
    // Add error string to entry for easy display
    entry.fieldErrors = fieldErrors;
    entry.errors = entryErrors.length > 0 ? entryErrors.join("; ") : "";
    
    if (entryErrors.length > 0) errorFlag = true;
    groupData[groupName].push(entry);
    errorData[groupName].push(entryErrors);
  }
  const pbcuploaddata = groupOrder.map(group => ({ group, data: groupData[group] }));
  const pbcdataerror = groupOrder.map(group => errorData[group]);
  const pbcerrorlist = [...new Set(pbcdataerror.flat(2).filter(item => item && item.trim() !== ""))];
  return { pbcuploaddata, pbcdataerror, pbcerrorlist, errorFlag };
}
/**
 * Calculates TB totals, category/subcategory sums, and attaches __tbtotals to each task in the checklist.
 * @param {Array} tb - Trial balance array
 * @param {Array} pbc - PBC items array
 * @param {Array} checklist - Task checklist array (will be mutated)
 * @param {Array} locationList - Location list array
 * @returns {void}
 */
function calculateDetails(tb, pbc, checklist, locationList) {
  let tbtotals = {};
  let closing_amount_to_subtract = [];
  let tb_cat_subcat_sum = {};
  for (const i of tb) {
    // ...existing code...
    let closing_info = {
      category: i.category,
      subcategory: i.subcategory,
      closing_amount: 0,
    };
    if (i.reference_detail && i.reference_detail.length > 0) {
      for (const j of i.reference_detail || []) {
        if (!j._id) {
          continue;
        }
        tbtotals[j._id] = tbtotals[j._id] || { total: 0, items: [] };
        tbtotals[j._id].items.push(i);
        tbtotals[j._id].total = tbtotals[j._id].total + i.closing_amount || 0;
        closing_info.closing_amount = tbtotals[j._id].total;
      }
      closing_amount_to_subtract.push(closing_info);
    }
    const category = i.category;
    const subcategory = i.subcategory;
    const location = "location" in i ? i.location : "";
    const key = `${category}-${subcategory}`;
    tb_cat_subcat_sum[key] = tb_cat_subcat_sum[key] || {
      sum: 0,
      category: category,
      subcategory: subcategory,
      locations: {},
    };
    tb_cat_subcat_sum[key].sum += i.closing_amount || 0;
    if (location) {
      tb_cat_subcat_sum[key].locations[location] =
        (tb_cat_subcat_sum[key].locations[location] || 0) +
        (i.closing_amount || 0);
    }
  }
  for (const ele of closing_amount_to_subtract) {
    if (tb_cat_subcat_sum[`${ele.category}-${ele.subcategory}`]) {
      tb_cat_subcat_sum[`${ele.category}-${ele.subcategory}`].sum -=
        ele.closing_amount;
    }
  }
  let pbcdata = {};
  let pbccount = {};
  for (const i of pbc || []) {
    pbcdata[i._id] = i;
    for (const j of i.reference || []) {
      pbccount[j] = pbccount[j] || { total: 0, completed: 0 };
      pbccount[j].total++;
      if (i.stage === 2) pbccount[j].completed++;
    }
  }
  for (const x of (Array.isArray(checklist) ? checklist : [])) {
    if ((x.preparer || []).length === 0 || (x.approver || []).length === 0)
      x.alltaskassigned = false;
    x.__pbccount = pbccount[x._id] || {};

    if (x?.location?.length > 0) {
      x.locationItem = locationList.filter(
        (obj) => obj["_id"] === x.location
      ) || [{ name: "" }];
      if (x.locationItem.length > 0) {
        x.locationName = x.locationItem[0].name || "";
      } else x.locationName = "";
    }
    x.__tbtotals = tbtotals[x._id] || { total: 0, items: [] };
  }
}
const { default: constants } = require("../../../plugins/constants.js");
// Cache task type constants locally to avoid repeating long property chains
const { TASK_TYPES } = constants.TASK_LIST_CONSTANTS;
/**
 * Formats custom columns data into a consistent structure using DB schema.
 * Includes all schema-defined columns even when no task has values yet.
 * @param {Object} customFields - Object containing custom field data values
 * @param {Array} customColumnSchema - Array of schema definitions from DB with {columnName, dataType, ...}
 * @returns {Array} Formatted custom columns with data types from schema, defaults to "string" if not in schema
 */
function formatCustomColumnDisplayText(key, schemaEntry) {
  if (schemaEntry?.displayName) {
    return schemaEntry.displayName;
  }
  if (schemaEntry?.label) {
    return schemaEntry.label;
  }
  return key
    .replace(/^custom_col_/, "")
    .replace(/_/g, " ")
    .replace(/\b\w+\b/g, word =>
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    );
}

function getCustomColumns(customFields = {}, customColumnSchema = []) {
  try {
    const fieldsObj = (customFields && typeof customFields === "object" && !Array.isArray(customFields))
      ? customFields
      : {};

    const schemaByColumnKey = buildCustomColumnSchemaMap(customColumnSchema);
    const columnKeys = new Set(Object.keys(fieldsObj));

    if (Array.isArray(customColumnSchema)) {
      customColumnSchema.forEach(entry => {
        const key = entry?.columnName || entry?.key;
        if (key) {
          columnKeys.add(key);
        }
      });
    }

    if (columnKeys.size === 0) {
      return [];
    }

    return Array.from(columnKeys).map(key => {
      const schemaEntry = schemaByColumnKey.get(key) || null;
      return {
        columnName: key,
        displayText: formatCustomColumnDisplayText(key, schemaEntry),
        dataType: schemaEntry?.dataType || "string",
      };
    });
  } catch (err) {
    console.log(err);
    throw err || new Error(ERROR_MESSAGES.FAILED_GET_CUSTOM_COLUMNS);
  }
}

/**
 * Checks if a value is a valid date in various formats
 * @param {*} value - The value to check
 * @returns {Boolean} Whether the value is a valid date
 */
function isValidDate(value) {
  // Check if it's a string in dd-mm-yyyy format
  if (typeof value === "string") {
    // Check for dd-mm-yyyy format
    if (/^\d{2}-\d{2}-\d{4}$/.test(value)) {
      return true;
    }
    // Check for full date string format (like "Fri May 23 2025 15:20:04 GMT+0530")
    if (/^\w{3}\s\w{3}\s\d{2}\s\d{4}/.test(value)) {
      return !isNaN(new Date(value).getTime());
    }
  }
  // Check if it's a Date object
  if (value instanceof Date && !isNaN(value.getTime())) {
    return true;
  }
  // For ISO date strings or other formats, try to create a date and check if valid
  if (value && !isNaN(new Date(value).getTime())) {
    return true;
  }
  return false;
}

function formatCustomColumns(customColumns = []) {
  try {
    const customFields = customColumns.map(item => ({
      columnName: item.columnName,
      displayText: (item.displayName) ?? "",
      dataType: item.dataType,
    }));
    return customFields
  } catch (err) {
    console.log(err);
    throw err || new Error(ERROR_MESSAGES.FAILED_FORMAT_CUSTOM_COLUMNS);
  }
}


function parseCommaSeparatedField(value, defaultValue = ''){
  const raw = (value !== undefined && value !== null && value !== '' ? value : defaultValue)
  .toString();
  return raw
  .split(',')
  .map(t => t.trim())
  .filter(t => t.length);
}


function identifyPossibleTaskTypes(isEdit, task_type) {
  const types = Object.values(constants.TASK_LIST_CONSTANTS.TASK_TYPES);
  console.log(`identifyPossibleTaskTypes called with isEdit: ${isEdit}, task_type: ${task_type}`);
  // THIS IS CAUSING VUE ISSUES for the task type dropdown. So disabled it for now.
  // // Only restrict on edit
  // if (isEdit) {
  //   const NORMAL = constants.TASK_LIST_CONSTANTS.TASK_TYPES.NORMAL;
  //   const ACCOUNT_RECON =
  //     constants.TASK_LIST_CONSTANTS.TASK_TYPES.ACCOUNT_RECONCILIATION;
  //   if (task_type === ACCOUNT_RECON) {
  //     // Only allow Account Reconciliation if that's the current type
  //     return [ACCOUNT_RECON];
  //   }
  //   // If current is Normal, allow both
  //   return [NORMAL, ACCOUNT_RECON];
  // }
  // // On create, allow all
  // return types;

  return types;
}

/**
 * Get display text for a task type constant.
 * @param {string} taskType
 * @returns {string}
 */
function getTaskTypeDisplay(taskType) {
  if (!taskType) return '';
  switch (taskType) {
    case TASK_TYPES.NORMAL:
      return 'Checklist';
    case TASK_TYPES.ACCOUNT_RECONCILIATION:
      return 'Account Reconciliation';
    default:
      return taskType;
  }
}

/**
 * Returns dropdown options for task types (value/display).
 * @returns {{value:string,name:string}[]}
 */
function getTaskTypeOptions() {
  return [
    { value: TASK_TYPES.ACCOUNT_RECONCILIATION, name: getTaskTypeDisplay(TASK_TYPES.ACCOUNT_RECONCILIATION) },
    { value: TASK_TYPES.NORMAL, name: getTaskTypeDisplay(TASK_TYPES.NORMAL) }
  ];
}

/**
 * True when the task-type column filter selects only checklist (normal) tasks.
 * @param {Object} tableFilterHeader
 * @returns {boolean}
 */
function isChecklistOnlyTaskTypeFilter(tableFilterHeader) {
  if (!tableFilterHeader || typeof tableFilterHeader !== 'object') return false;
  const selected = tableFilterHeader.task_type;
  if (selected == null || (Array.isArray(selected) && selected.length === 0)) return false;

  const values = (Array.isArray(selected) ? selected : [selected])
    .map((value) => String(value).trim())
    .filter(Boolean);
  if (values.length === 0) return false;

  const checklistLabels = new Set([
    getTaskTypeDisplay(TASK_TYPES.NORMAL),
    LEGACY_TASK_TYPE_LABEL_CHECKLIST_NAME,
    TASK_TYPES.NORMAL,
  ]);
  const reconLabels = new Set([
    getTaskTypeDisplay(TASK_TYPES.ACCOUNT_RECONCILIATION),
    TASK_TYPES.ACCOUNT_RECONCILIATION,
  ]);

  const onlyChecklist = values.every((value) => checklistLabels.has(value));
  const hasRecon = values.some((value) => reconLabels.has(value));
  return onlyChecklist && !hasRecon;
}

const RECON_TASKLIST_COLUMN_VALUES = Object.keys(CHECKLIST_ONLY_TASK_TYPE_HEADER_VISIBILITY);

/**
 * Temporarily hide recon amount columns for checklist-only task-type filters.
 * Preserves pre-filter `visible` flags in `visibilitySnapshot` and restores them
 * when the filter clears — does not mutate saved customize-column preferences.
 * @param {Array} headers
 * @param {Object} tableFilterHeader
 * @param {Object} [visibilitySnapshot]
 * @returns {{ headers: Array, visibilitySnapshot: Object }}
 */
function applyReconColumnVisibilityForTaskTypeFilter(
  headers,
  tableFilterHeader,
  visibilitySnapshot = {}
) {
  const snapshot = visibilitySnapshot && typeof visibilitySnapshot === 'object'
    ? visibilitySnapshot
    : {};

  if (!Array.isArray(headers)) {
    return { headers, visibilitySnapshot: snapshot };
  }

  const isChecklistOnly = isChecklistOnlyTaskTypeFilter(tableFilterHeader);

  if (isChecklistOnly) {
    RECON_TASKLIST_COLUMN_VALUES.forEach((columnValue) => {
      const header = headers.find((entry) => entry?.value === columnValue);
      if (!header) return;
      if (!Object.prototype.hasOwnProperty.call(snapshot, columnValue)) {
        snapshot[columnValue] = header.visible;
      }
      header.visible = false;
    });
  } else {
    RECON_TASKLIST_COLUMN_VALUES.forEach((columnValue) => {
      if (!Object.prototype.hasOwnProperty.call(snapshot, columnValue)) return;
      const header = headers.find((entry) => entry?.value === columnValue);
      if (header) {
        header.visible = snapshot[columnValue];
      }
      delete snapshot[columnValue];
    });
  }

  return { headers, visibilitySnapshot: snapshot };
}


//export both functions

/**
 * Returns the formatted header text for a custom column.
 * If the column is a date type, adds (dd/mm/yyyy) to the display text.
 * @param {Object} column - The custom column object.
 * @returns {string} - The formatted header text.
 */
function getCustomColumnHeaderText(column) {
  if (!column || !column.displayText) return "";
  return column.dataType === "date"
    ? `${column.displayText} (dd/mm/yyyy)`
    : column.displayText;
}
function ceilToNextMinute(date) {
  const newDate = new Date(date.getTime());
  if (newDate.getSeconds() === 0 && newDate.getMilliseconds() === 0) {
  return newDate;
  }
  newDate.setSeconds(0, 0);
  newDate.setMinutes(newDate.getMinutes() + 1);
  return newDate;
}

/**
 * Convert a date string with offset (like GMT+0530 or GMT-0700)
 * and return a Date object adjusted to the local system time.
 *
 * @param {string | Date} dateInput - Date string with offset or Date object
 * @returns {Date} - Date adjusted to local time
 */
function applyTimezoneOffsetAndConvertToLocal(dateInput) {
  const originalDate = ceilToNextMinute(dateInput);

  if (isNaN(originalDate)) {
    throw new Error(ERROR_MESSAGES.INVALID_DATE_INPUT);
  }

  // Get local timezone offset in minutes (e.g., -330 for GMT+0530)
  const localOffsetMinutes = originalDate.getTimezoneOffset();
  // Convert to local time by subtracting local offset (minutes)
  const adjustedTime = originalDate.getTime() - (localOffsetMinutes * 60 * 1000);
  // Convert adjustedTime to a Date object in UTC
  const utcDate = new Date(adjustedTime);
  // Format to DD/MM/YYYY using UTC values
  const day = String(utcDate.getUTCDate()).padStart(2, '0');
  const month = String(utcDate.getUTCMonth() + 1).padStart(2, '0');
  const year = utcDate.getUTCFullYear();

  return `${day}/${month}/${year}`;
}


/**
* Format a given date (Date object, timestamp, or string) into dd/MM/yyyy format.
 * Returns an empty string if the input is invalid or missing.
 *
 *param {Date | string | number | null | undefined} inputDate - The input date to format
* @returns {string} - Formatted date string as "dd/MM/yyyy"
*/
function formatDateToDMY(inputDate) {
  // Handle Excel cell object with .result as Date
  let dateObj = null;
  if (inputDate instanceof Date) {
    dateObj = inputDate;
  } else if (typeof inputDate === 'object' && inputDate?.result instanceof Date) {
    dateObj = inputDate.result;
  }

  if (dateObj) {
    const formattedDate = applyTimezoneOffsetAndConvertToLocal(dateObj);
    return formattedDate;
  }

  if (
    typeof inputDate !== 'string' &&
    typeof inputDate !== 'number' &&
    Object.prototype.toString.call(inputDate) !== '[object Date]'
  ) {
    return '';
  }

  let date;

  if (typeof inputDate === 'string') {
    const trimmed = inputDate.trim();

    // 1. If already in dd/mm/yyyy or d/m/yyyy, normalize to dd/mm/yyyy
    const ddmmyyyyRegex = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
    const match = trimmed.match(ddmmyyyyRegex);
    if (match) {
      return `${match[1].padStart(2, '0')}/${match[2].padStart(2, '0')}/${match[3]}`;
    }

    // 2. If dd-mm-yyyy or d-m-yyyy => convert to dd/mm/yyyy
    const dashFormat = trimmed.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
    if (dashFormat) {
      return `${dashFormat[1].padStart(2, '0')}/${dashFormat[2].padStart(2, '0')}/${dashFormat[3]}`;
    }

    // 3. If yyyy-mm-dd => convert to dd/mm/yyyy
    const ymdFormat = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (ymdFormat) {
      return `${ymdFormat[3]}/${ymdFormat[2]}/${ymdFormat[1]}`;
    }

    // 4. Else try parsing (assume long format like "Wed Oct...")
    date = new Date(trimmed);
    if (isNaN(date.getTime())) {
      return ''; // Invalid
    }
  } else if (typeof inputDate === 'number') {
    date = new Date(inputDate);
    if (isNaN(date.getTime())) {
      return '';
    }
  } else if (Object.prototype.toString.call(inputDate) === '[object Date]') {
    date = new Date(inputDate);
  }

  // Format to dd/mm/yyyy using UTC to avoid timezone issues
  const day = String(date.getUTCDate()).padStart(2, '0');
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const year = date.getUTCFullYear();

  return `${day}/${month}/${year}`;
}

// xlsx serial-date conversion has floating-point error that can land slightly before local midnight
// (e.g. 23:59:50 instead of 00:00:00). Adding a 60-second tolerance then flooring to the day
// via local date-part constructors fixes this for all UTC offsets (positive and negative).
function normalizeXlsxDate(date) {
  const adjusted = new Date(date.getTime() + 60 * 1000);
  return new Date(adjusted.getFullYear(), adjusted.getMonth(), adjusted.getDate());
}

/**
 * Parses a due date value from various input formats into a JavaScript Date object.
 * Handles dd/mm/yyyy, dd-mm-yyyy, yyyy-mm-dd strings, Date objects, and Excel cell objects.
 * 
 * JavaScript's native `new Date(string)` assumes US mm/dd/yyyy for slash-separated dates,
 * which causes incorrect parsing for dd/mm/yyyy formatted inputs from Excel sheets.
 * This function explicitly handles the expected formats to avoid that ambiguity.
 *
 * @param {Date | string | number | object | null | undefined} duedate - Raw due date value from Excel/input
 * @returns {Date|null} Parsed Date object, or null if invalid/empty
 */
function parseDueDateValue(duedate) {
  if (!duedate) return null;

  // Handle native Date objects
  if (duedate instanceof Date) return normalizeXlsxDate(duedate);

  // Handle Excel cell objects with .result as Date
  if (typeof duedate === 'object' && duedate?.result instanceof Date) return normalizeXlsxDate(duedate.result);

  if (typeof duedate === 'string') {
    const trimmed = duedate.trim();

    // dd/mm/yyyy or d/m/yyyy — most common format from Excel sheets / user edits
    const slashMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (slashMatch) {
      const day = Number(slashMatch[1]), month = Number(slashMatch[2]) - 1, year = Number(slashMatch[3]);
      const d = new Date(year, month, day);
      if (d.getFullYear() !== year || d.getMonth() !== month || d.getDate() !== day) return null;
      return d;
    }

    // dd-mm-yyyy or d-m-yyyy — dash variant of day-first format
    const dashMatch = trimmed.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
    if (dashMatch) {
      const day = Number(dashMatch[1]), month = Number(dashMatch[2]) - 1, year = Number(dashMatch[3]);
      const d = new Date(year, month, day);
      if (d.getFullYear() !== year || d.getMonth() !== month || d.getDate() !== day) return null;
      return d;
    }

    // yyyy-mm-dd — ISO format (kept strict 2-digit month/day)
    const isoMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (isoMatch) {
      const year = Number(isoMatch[1]), month = Number(isoMatch[2]) - 1, day = Number(isoMatch[3]);
      const d = new Date(year, month, day);
      if (d.getFullYear() !== year || d.getMonth() !== month || d.getDate() !== day) return null;
      return d;
    }

    // Fallback — other string formats (e.g. "May 1, 2026")
    const fallback = new Date(trimmed);
    return isNaN(fallback.getTime()) ? null : fallback;
  }

  // Handle numeric (timestamp)
  if (typeof duedate === 'number') {
    const fromNum = new Date(duedate);
    return isNaN(fromNum.getTime()) ? null : fromNum;
  }

  return null;
}

/**
 * Validates whether a due date falls within the allowed range.
 * Default window is today through today + maxDays (calendar).
 * Pass minDate/maxDate for cycle tasks (working days from cycle end).
 *
 * @param {Date|string|number|object} duedate - Raw due date value to validate
 * @param {number} maxDays - Maximum number of days allowed
 * @param {{ minDate?: Date|string, maxDate?: Date|string, usesCycleEnd?: boolean }} [options]
 *   When usesCycleEnd is explicitly true/false, that controls the range error message.
 *   When omitted, a supplied maxDate implies cycle-end wording (legacy).
 * @returns {{ valid: boolean, error: string }} Validation result with error message if invalid
 */
function validateDueDateRange(duedate, maxDays, options = {}) {
  const parsedDate = parseDueDateValue(duedate);

  if (!parsedDate) {
    const displayValue = typeof duedate === 'string'
      ? (duedate.length > 50 ? duedate.slice(0, 50) + '…' : duedate)
      : typeof duedate;
    return { valid: false, error: `Invalid date format: '${displayValue}'. Expected dd/mm/yyyy` };
  }

  // Normalize parsedDate to start-of-day so a non-midnight time component
  // (e.g. from a Date input) doesn't cause incorrect boundary rejections.
  const normalizedDate = new Date(parsedDate.getFullYear(), parsedDate.getMonth(), parsedDate.getDate());

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const parsedMin = options.minDate != null && options.minDate !== ''
    ? parseDueDateValue(options.minDate)
    : null;
  const parsedMax = options.maxDate != null && options.maxDate !== ''
    ? parseDueDateValue(options.maxDate)
    : null;

  const minDate = parsedMin
    ? new Date(parsedMin.getFullYear(), parsedMin.getMonth(), parsedMin.getDate())
    : today;
  const maxDate = parsedMax
    ? new Date(parsedMax.getFullYear(), parsedMax.getMonth(), parsedMax.getDate())
    : (() => {
      const fallback = new Date(today);
      fallback.setDate(fallback.getDate() + maxDays);
      return fallback;
    })();

  if (normalizedDate < minDate || normalizedDate > maxDate) {
    const usesCycleEndError = typeof options.usesCycleEnd === 'boolean'
      ? options.usesCycleEnd
      : !!parsedMax;
    const error = usesCycleEndError
      ? DUE_DATE_RANGE_ERROR.FROM_CYCLE_END(maxDays)
      : DUE_DATE_RANGE_ERROR.FROM_TODAY(maxDays);
    return { valid: false, error };
  }

  return { valid: true, error: '' };
}

/**
 * Handles merging, removing, and adding custom column headers to tableHeadersfilter.
 * @param {Array} customColumns
 * @param {Array} tableHeadersfilter
 * @returns {Object} { localCustomColumns, taskMasterCustomColumns, tableHeadersfilter }
 */
/**
 * Handles merging, removing, and adding custom column headers to tableHeadersfilter.
 * Uses DB schema to determine column data types instead of inferring from values.
 * @param {Array} customColumns - Custom column data values
 * @param {Array} tableHeadersfilter - Existing table headers array
 * @param {Array} customColumnSchema - (Optional) Schema definitions from DB with {columnName, dataType, ...}
 * @returns {Object} { localCustomColumns, taskMasterCustomColumns, tableHeadersfilter }
 */
 function handleCustomColumnHeadersUtil(customColumns, tableHeadersfilter, customColumnSchema = []) {
  const formattedCustomColumns = getCustomColumns(customColumns || [], customColumnSchema || []);
  let localCustomColumns = [];
  let taskMasterCustomColumns = [];
  let headers = tableHeadersfilter ? [...tableHeadersfilter] : [];

  if (formattedCustomColumns && formattedCustomColumns.length > 0) {
    localCustomColumns = formattedCustomColumns;
    // Prepare headers with custom flag
    const customHeaders = formattedCustomColumns.map(header => ({
      value_en: `custom_${header.columnName}`,
      value: header.columnName,
      text: header.displayText,
      visible: true,
      filterable: true,
      sortable: true
    }));
    taskMasterCustomColumns = customHeaders;
  } else {
    localCustomColumns = [];
    headers = headers.filter(h => !String(h.value_en).startsWith("custom_"));
  }
  if (Array.isArray(localCustomColumns) && localCustomColumns.length) {
    const customColumnNames = localCustomColumns.map(c => c.columnName);
    // Remove invalid custom columns from tableHeadersfilter
    headers = headers.filter(h => {
      const isCustom = String(h.value_en).startsWith('custom');
      return !isCustom || customColumnNames.includes(h.value);
    });
    // Add any new custom columns
    localCustomColumns.forEach(col => {
      const exists = headers.some(h =>
        h.value === col.columnName || h.value_en === `custom_${col.columnName}`
      );
      if (!exists) {
        headers.push({
          value_en: `custom_${col.columnName}`,
          value: col.columnName,
          text: col.displayText,
          visible: true,
          filterable: true,
          sortable: true
        });
      }
    });
    // Move 'action' column to the end
    const actionIdx = headers.findIndex(h => h.value === "action");
    if (actionIdx !== -1) {
      const [actionCol] = headers.splice(actionIdx, 1);
      headers.push(actionCol);
    }
  }
  return {
    localCustomColumns,
    taskMasterCustomColumns,
    tableHeadersfilter: headers
  };
}

/**
 * Builds a Map of schema entries keyed by columnName and key for O(1) lookup.
 * @param {Array} customColumnSchema
 * @returns {Map<string, Object>}
 */
function buildCustomColumnSchemaMap(customColumnSchema = []) {
  const schemaByColumnKey = new Map();
  if (!Array.isArray(customColumnSchema)) {
    return schemaByColumnKey;
  }
  customColumnSchema.forEach(entry => {
    if (entry?.columnName) {
      schemaByColumnKey.set(entry.columnName, entry);
    }
    if (entry?.key) {
      schemaByColumnKey.set(entry.key, entry);
    }
  });
  return schemaByColumnKey;
}

/**
 * Applies DB schema dataType to formatted custom columns without changing display names.
 * @param {Array} columns - Formatted custom columns [{ columnName, displayText, dataType }]
 * @param {Array} customColumnSchema - Schema from formData.clientCustomColumns
 * @returns {Array}
 */
function applyCustomColumnSchema(columns = [], customColumnSchema = []) {
  if (!Array.isArray(customColumnSchema) || !customColumnSchema.length) {
    return Array.isArray(columns) ? columns : [];
  }
  if (!Array.isArray(columns) || !columns.length) {
    return getCustomColumns({}, customColumnSchema);
  }

  const schemaByColumnKey = buildCustomColumnSchemaMap(customColumnSchema);

  return columns.map(col => {
    const schemaEntry = schemaByColumnKey.get(col.columnName);
    if (!schemaEntry) {
      return col;
    }
    return {
      ...col,
      displayText: schemaEntry.displayName || schemaEntry.label || col.displayText,
      dataType: schemaEntry.dataType || col.dataType,
    };
  });
}

 // Utility: add (or aggregate) error message(s) for a given field

const addError = (rowErrorBag, fieldName, message = " ") => {
  if (!rowErrorBag[fieldName]) {
rowErrorBag[fieldName] = message;
  } else {
rowErrorBag[fieldName] = Array.isArray(rowErrorBag[fieldName])
  ? [...rowErrorBag[fieldName], message]
  : [rowErrorBag[fieldName], message];
  }
};

 // Utility: normalize error objects so array messages become joined text (for table display)
 const normalizeErrorData = (rawErrorArray) => {
  return rawErrorArray.map(errorObject => {
const normalized = {};
Object.keys(errorObject).forEach(fieldKey => {
  normalized[fieldKey] = Array.isArray(errorObject[fieldKey])
    ? errorObject[fieldKey].join(" | ")
    : errorObject[fieldKey];
});
return normalized;
  });
};

/**
 * Checks if a task has bot analysis details and bot ID linked
 * @param {Object} taskData - The task data object
 * @returns {boolean} - True if task has bot_analysis_detail._id and botid, false otherwise
 */
function isTaskBotLinked(taskData) {
    return !!(taskData?.bot_details?._id);
}

/**
 * Gets the display configuration for a task stage
 * @param {number} stage - The stage value (0, 2, or 3)
 * @returns {Object|null} - The display configuration object or null if stage not found
 */
function getStageDisplay(stage) {
  const { TASK_STAGE_DISPLAY } = require('./constants');
  return TASK_STAGE_DISPLAY[stage] || null;
}

/**
 * Checks if all provided user IDs are present in the active users list.
 *
 * Converts `userIds` to an array if necessary, then verifies each ID exists in `activeUsers`.
 * Returns an object with:
 *  - `isActive`: true if all user IDs are active, false otherwise.
 *  - `inactiveUserIds`: array of user IDs not found in the active users list.
 *
 * @param {string|string[]|number|number[]} userIds - User ID(s) to check.
 * @param {Array<{id: string|number}>} activeUsers - Array of active user objects (must have `id` property).
 * @returns {{ isActive: boolean, inactiveUserIds: Array<string|number> }} Result object.
 */
const checkWhetherUserIsActive = (userIds, activeUsers) => {
  if (!userIds || !Array.isArray(activeUsers) || activeUsers.length === 0) {
    return { isActive: false, inactiveUserIds: [] };
  }
  // Convert userIds to array if it's a string or single value
  let idsArray = Array.isArray(userIds) ? userIds : [userIds];
  // Check every userids are active (support both user._id and user.id)
  const matchUser = (user, id) => String(user?._id) === String(id) || String(user?.id) === String(id);
  const result = idsArray?.every(id =>
    activeUsers?.some(user => matchUser(user, id))
  );

  return {
    isActive: result,
    inactiveUserIds: idsArray?.filter(id =>
      !activeUsers?.some(user => matchUser(user, id))
    ) || []
  }
};
const rolesForTaskEdit = [
      { key: 'preparer', label: 'preparer' },
      { key: 'approver', label: 'approver' }
    ];

export const activeUserCheckType = {
    TASKLIST: 'tasklist',
    EDIT_TASK: 'taskEditDetails'
}
/**
 * Validates that all users in the given roles are active.
 * Returns { valid: boolean, messages: string[] }
 *
 * @param {Object} addeditdata - The data object containing user IDs for each role.
 * @param {Array<{id: string|number}>} activeUsers - Array of active user objects.
 * @param {Array<Object>} allUsers - Array of all user objects (for name/email lookup).
 * @param {Array<{key: string, label: string}>} roles - Roles to validate (e.g. [{key:'preparerids',label:'preparer'}])
 * @returns {{ valid: boolean, messages: string[] }}
 */
export function validateUsersAreActiveAndAttachMessages(addeditdata, activeUsers, allUsers, roles = activeUserCheckType.TASKLIST) {
  const roleDetails =  rolesForTaskEdit;
  const getInactiveNames = inactiveIds => {
    const inactiveSet = new Set(inactiveIds?.map(String));
    return allUsers
      .filter(user => inactiveSet.has(String(user._id)))
      .map(user => user?.name || user?.email || String(user?._id));
  };
  let messages = [];
  let valid = true;

  for (const role of roleDetails) {
    const selectedUsers = roles === activeUserCheckType.TASKLIST ? (addeditdata.location?.[0]?.[role.key] ?? []) : addeditdata[role.key];
    const { isActive, inactiveUserIds } = checkWhetherUserIsActive(selectedUsers, activeUsers);
    if (!isActive) {
      const inactiveNames = getInactiveNames(inactiveUserIds);
      if (inactiveNames.length) {
        messages.push(`This ${role.label} is not an active user. Inactive User(s): ${inactiveNames.join(', ')}. Please remove them before proceeding.`);
      }
      valid = false;
    }
  }

  return { valid, messages };
}

/**
 * Checks if an item's attribute is not null and not undefined
 * @param {Object} item - The item object to check
 * @param {string} attribute - The attribute name to check
 * @returns {boolean} - True if attribute exists and is not null/undefined
 */
function checkForNotNull(item, attribute) {
  return item && item[attribute] !== null && item[attribute] !== undefined;
}

/**
 * Gets the TB totals from a task item
 * @param {Object} item - The task item
 * @returns {number} - The TB total amount
 */
function getTbTotals(item) {
  return (item && item.__tbtotals && typeof item.__tbtotals.total === 'number')
    ? item.__tbtotals.total : 0;
}

/**
 * Calculates the schedule amount (recon amount) from a task item
 * Prioritizes bot_recon_schedule_amount if present, otherwise uses result.value.total_value
 * @param {Object} item - The task item
 * @returns {number} - The schedule/recon amount
 */
function calculateScheduleAmount(item) {
  if (!item) return 0;
  if (checkForNotNull(item, 'bot_recon_schedule_amount')) {
    return Number(item.bot_recon_schedule_amount) || 0;
  }
  if (item.result && item.result.value && item.result.value.total_value !== undefined) {
    return Number(item.result.value.total_value) || 0;
  }
  return 0;
}

/**
 * Calculates the explained difference from a task item
 * Prioritizes bot_recon_explained_difference if present, otherwise uses result.value.adjusted_value
 * @param {Object} item - The task item
 * @returns {number} - The explained difference amount
 */
function calculateExplainedDifference(item) {
  if (!item) return 0;
  if (checkForNotNull(item, 'bot_recon_explained_difference')) {
    return Number(item.bot_recon_explained_difference) || 0;
  }
  if (item.result && item.result.value && item.result.value.adjusted_value !== undefined) {
    return Number(item.result.value.adjusted_value) || 0;
  }
  return 0;
}

/**
 * Calculates the unexplained difference from a task item
 * Formula: TB Total - Schedule Amount - Explained Difference
 * Prioritizes bot data if present, otherwise uses result data
 * @param {Object} item - The task item
 * @returns {number} - The unexplained difference amount
 */
function calculateUnexplainedDifference(item) {
  if (!item) return 0;
  const total = getTbTotals(item);
  const hasBotData = checkForNotNull(item, 'bot_recon_schedule_amount') || checkForNotNull(item, 'bot_recon_explained_difference');
  
  if (hasBotData) {
    return total 
      - (Number(item.bot_recon_schedule_amount) || 0) 
      - (Number(item.bot_recon_explained_difference) || 0);
  }
  
  if (item.result && item.result.value) {
    return total 
      - (Number(item.result.value.total_value) || 0) 
      - (Number(item.result.value.adjusted_value) || 0);
  }
  
  return total;
}

/**
 * Groups reconciliation data by the Group field and adds group headers.
 * Filters out empty rows and organizes data into collapsible groups.
 * 
 * BACKWARD COMPATIBILITY: If no items have a Group field value, returns
 * the filtered data as-is without any grouping applied.
 * 
 * @param {Array} data - Array of reconciliation data items
 * @param {Object} options - Configuration options
 * @param {Object} options.groups - Group name constants (from TM_RECONCILIATION.GROUPS)
 * @param {Array} options.groupDisplayOrder - Order for displaying groups (from TM_RECONCILIATION.GROUP_DISPLAY_ORDER)
 * @returns {Array} Grouped and flattened data with headers, or original data if no grouping exists
 * 
 * Example usage:
 * const groupedData = groupReconciliationData(rawData, {
 *   groups: TM_RECONCILIATION.GROUPS,
 *   groupDisplayOrder: TM_RECONCILIATION.GROUP_DISPLAY_ORDER
 * });
 */
function groupReconciliationData(data, options = {}) {
  const {
    groups = {
      TABLE_1_TOTAL: 'Table 1 Total',
      EXPLAINED_DIFFERENCE: 'Explained difference',
      UNEXPLAINED_DIFFERENCE: 'Unexplained difference',
      NET_ADJUSTED_AMOUNT: 'Net Adjusted Amount',
      TABLE_2_TOTAL: 'Table 2 Total',
      UNGROUPED: 'Ungrouped'
    },
    groupDisplayOrder = [
      'Table 1 Total',
      'Explained difference',
      'Unexplained difference',
      'Ungrouped',
      'Net Adjusted Amount',
      'Table 2 Total'
    ]
  } = options;

  if (!data || !Array.isArray(data) || data.length === 0) {
    return [];
  }

  // Filter out empty rows (where Reconciliation, Sub Amount, and Amount are all empty/null)
  const filteredData = data.filter(item => {
    const reconciliation = (item.Reconciliation || '').toString().trim();
    const subAmount = item['Sub Amount'];
    const amount = item.Amount;
    
    // Keep the row if at least one of these has a meaningful value
    const hasReconciliation = reconciliation !== '';
    const hasSubAmount = subAmount !== null && subAmount !== undefined && subAmount !== '';
    const hasAmount = amount !== null && amount !== undefined && amount !== '';
    
    return hasReconciliation || hasSubAmount || hasAmount;
  });

  // BACKWARD COMPATIBILITY CHECK:
  // Check if any item has a non-empty Group field
  // If no items have Group values, return the filtered data without grouping
  const hasAnyGrouping = filteredData.some(item => {
    const groupName = (item.Group || '').trim();
    return groupName !== '';
  });

  if (!hasAnyGrouping) {
    // No grouping exists - return data as-is with basic metadata for consistency
    return filteredData.map((item, idx) => ({
      ...item,
      _isGroupHeader: false,
      _isGroupItem: false,
      _groupName: null,
      _key: `item-${idx}`
    }));
  }

  // Group items by their Group field
  const groupedItems = {};
  
  filteredData.forEach(item => {
    const groupName = (item.Group || '').trim();
    
    // Determine which group this item belongs to
    let targetGroup;
    if (groupName === groups.TABLE_1_TOTAL) {
      targetGroup = groups.TABLE_1_TOTAL;
    } else if (groupName === groups.EXPLAINED_DIFFERENCE) {
      targetGroup = groups.EXPLAINED_DIFFERENCE;
    } else if (groupName === groups.UNEXPLAINED_DIFFERENCE) {
      targetGroup = groups.UNEXPLAINED_DIFFERENCE;
    } else if (groupName === groups.NET_ADJUSTED_AMOUNT) {
      targetGroup = groups.NET_ADJUSTED_AMOUNT;
    } else if (groupName === groups.TABLE_2_TOTAL) {
      targetGroup = groups.TABLE_2_TOTAL;
    } else {
      // Items with empty or unrecognized Group go to Ungrouped
      targetGroup = groups.UNGROUPED;
    }
    
    if (!groupedItems[targetGroup]) {
      groupedItems[targetGroup] = [];
    }
    groupedItems[targetGroup].push(item);
  });

  // Build the flattened result with group headers
  const result = [];
  
  groupDisplayOrder.forEach(groupName => {
    const items = groupedItems[groupName];
    
    if (!items || items.length === 0) {
      return; // Skip empty groups
    }
    
    // Special handling for Table 1 Total and Table 2 Total - no group header needed
    if (groupName === groups.TABLE_1_TOTAL || groupName === groups.TABLE_2_TOTAL) {
      items.forEach((item, idx) => {
        result.push({
          ...item,
          _isGroupHeader: false,
          _groupName: groupName,
          _key: `${groupName}-item-${idx}`
        });
      });
      return;
    }
    
    // Add group header for other groups
    result.push({
      _isGroupHeader: true,
      _groupName: groupName,
      _groupHeaderText: groupName,
      _key: `${groupName}-header`,
      Reconciliation: groupName,
      'Sub Amount': null,
      Amount: null,
      Group: groupName
    });
    
    // Add items under this group
    items.forEach((item, idx) => {
      result.push({
        ...item,
        _isGroupHeader: false,
        _isGroupItem: true,
        _groupName: groupName,
        _key: `${groupName}-item-${idx}`
      });
    });
  });

  return result;
}

/**
 * Returns true when a Vue watcher should no-op (initial bind or unchanged value).
 * @param {*} newValue - The watcher's new value
 * @param {*} oldValue - The watcher's old value
 * @returns {boolean}
 */
function shouldSkipWatcherChange(newValue, oldValue) {
  return newValue === oldValue || oldValue === undefined;
}

/**
 * Uppercase file extension for display badges (e.g. PNG, PDF).
 * @param {string} filename
 * @param {string} [fallback='FILE']
 */
function getDisplayFileExtension(filename, fallback = 'FILE') {
  const name = filename || '';
  const parts = name.split('.');
  if (parts.length < 2) return fallback;
  return parts.pop().toUpperCase();
}

/**
 * Badge color variant for a file extension (see FILE_BADGE_VARIANTS).
 * @param {string} extension - uppercase or lowercase extension
 */
function getFileBadgeVariant(extension) {
  const ext = (extension || '').toUpperCase();
  return FILE_BADGE_VARIANTS[ext] || FILE_BADGE_VARIANT_DEFAULT;
}

export { 
  getCustomColumns, 
  isValidDate, 
  formatCustomColumns, 
  identifyPossibleTaskTypes, 
  getCustomColumnHeaderText, 
  formatDateToDMY, 
  parseDueDateValue,
  validateDueDateRange,
  calculateDetails, 
  handleCustomColumnHeadersUtil,
  applyCustomColumnSchema,
  parseCommaSeparatedField, 
  addError, 
  normalizeErrorData, 
  isTaskBotLinked, 
  getStageDisplay, 
  checkWhetherUserIsActive, 
  getTaskTypeDisplay, 
  getTaskTypeOptions,
  isChecklistOnlyTaskTypeFilter,
  applyReconColumnVisibilityForTaskTypeFilter,
  checkForNotNull,
  getTbTotals,
  calculateScheduleAmount,
  calculateExplainedDifference,
  calculateUnexplainedDifference,
  extractFileIdsFromTask,
  hasDownloadableFiles,
  groupReconciliationData,
  shouldSkipWatcherChange,
  getDisplayFileExtension,
  getFileBadgeVariant,
}

/**
 * Extracts file IDs from a task object, supporting both upload_v2 (newer) and upload (legacy) structures.
 * Prefers upload_v2 if available, falls back to upload for backward compatibility.
 * 
 * Supported formats:
 * - upload_v2: Array of objects with file_id property [{file_id: "id1"}, {file_id: "id2"}]
 * - upload: String "id1" | Array ["id1", "id2"] | Nested arrays [["id1", "id2"]] | [[["id1"]]]
 * 
 * @param {Object} task - Task object containing upload_v2 and/or upload properties
 * @returns {Array<string>} Array of unique file IDs (empty array if no files found)
 */
function extractFileIdsFromTask(task) {
  if (!task) return [];

  let fileIds = [];

  // First try upload_v2 (array of objects with file_id)
  if (Array.isArray(task.upload_v2) && task.upload_v2.length > 0) {
    const ids = task.upload_v2
      .map(file => file?.file_id || file?._id)
      .filter(id => id && typeof id === 'string');
    fileIds.push(...ids);
  }
  // Fallback to upload (supports multiple formats for backward compatibility)
  // Formats: "id1" | ["id1", "id2"] | [["id1", "id2"]] | [[["id1"]]]
  else if (task.upload) {
    if (Array.isArray(task.upload)) {
      // Use flat(Infinity) to handle any level of nesting
      const flatIds = task.upload.flat(Infinity).filter(id => id && typeof id === 'string');
      fileIds.push(...flatIds);
    } else if (typeof task.upload === 'string') {
      fileIds.push(task.upload);
    }
  }

  // Remove duplicates and empty values
  return [...new Set(fileIds)].filter(id => id);
}

/**
 * Checks if a task has downloadable files (in either upload_v2 or upload)
 * @param {Object} task - Task object
 * @returns {boolean} True if task has files that can be downloaded
 */
function hasDownloadableFiles(task) {
  return extractFileIdsFromTask(task).length > 0;
}